import React, { useEffect, useState } from "react";
import { GripVertical, Plus, Pencil, Trash2, Check, X, Layers, Save } from "lucide-react";
import { adminApi } from "../../lib/api";
import { iconNames } from "../../lib/icons";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ImageUpload from "./ImageUpload";
import VideoUpload from "./VideoUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const UNGROUPED = "__ungrouped__";

function GroupsPanel({ groups, reload }) {
  const { toast } = useToast();
  const [items, setItems] = useState(groups);
  const [newLabel, setNewLabel] = useState("");
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  useEffect(() => { setItems(groups); }, [groups]);

  const add = async () => {
    if (!newLabel.trim()) return;
    try {
      await adminApi.serviceGroups.create({ label: newLabel.trim(), order: items.length + 1 });
      setNewLabel(""); toast({ title: "Group added" }); reload();
    } catch { toast({ title: "Couldn't add group", variant: "destructive" }); }
  };

  const saveEdit = async (g) => {
    try { await adminApi.serviceGroups.update(g.id, { label: editLabel.trim() || g.label, order: g.order }); setEditId(null); toast({ title: "Group updated" }); reload(); }
    catch { toast({ title: "Couldn't update", variant: "destructive" }); }
  };

  const remove = async () => {
    try { await adminApi.serviceGroups.remove(deleteId); toast({ title: "Group deleted" }); reload(); }
    catch { toast({ title: "Couldn't delete", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  const handleDrop = async (dropIndex) => {
    setOverIndex(null);
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setItems(reordered); setDragIndex(null);
    try { await adminApi.serviceGroupsReorder(reordered.map((i) => i.id)); toast({ title: "Order updated" }); reload(); }
    catch { toast({ title: "Couldn't save order", variant: "destructive" }); reload(); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-8">
      <h2 className="font-display font-bold text-navy flex items-center gap-2 mb-1"><Layers size={17} className="text-orange" /> Service groups</h2>
      <p className="text-xs text-slate-400 mb-4 flex items-center gap-1.5"><GripVertical size={13} /> These become the columns in the Services menu. Drag to reorder.</p>

      <div className="space-y-2 mb-4">
        {items.map((g, index) => (
          <div
            key={g.id}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
            onDrop={() => handleDrop(index)}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            data-testid={`group-row-${g.key}`}
            className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all ${overIndex === index && dragIndex !== null ? "border-orange ring-2 ring-orange/30" : "border-slate-100"} ${dragIndex === index ? "opacity-50" : "bg-slate-50/60"}`}
          >
            <button draggable onDragStart={() => setDragIndex(index)} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-navy shrink-0 touch-none" title="Drag to reorder"><GripVertical size={16} /></button>
            {editId === g.id ? (
              <>
                <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40" autoFocus />
                <button onClick={() => saveEdit(g)} className="w-8 h-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 flex items-center justify-center"><Check size={15} /></button>
                <button onClick={() => setEditId(null)} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center"><X size={15} /></button>
              </>
            ) : (
              <>
                <span className="flex-1 font-semibold text-navy text-sm">{g.label}</span>
                <span className="text-xs text-slate-400">#{g.order}</span>
                <button onClick={() => { setEditId(g.id); setEditLabel(g.label); }} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-navy flex items-center justify-center"><Pencil size={14} /></button>
                <button onClick={() => setDeleteId(g.id)} className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 size={14} /></button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input data-testid="new-group-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a new group (e.g. Payroll & Pensions)" className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40" />
        <button data-testid="add-group-btn" onClick={add} className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-deep text-white font-bold px-5 py-2 rounded-full text-sm transition-colors"><Plus size={15} /> Add</button>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this group?</AlertDialogTitle><AlertDialogDescription>Services in this group won't be deleted, but they'll move to an "Ungrouped" list until reassigned.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const emptyService = (group) => ({ title: "", icon: "Sparkles", group, order: 99, short: "", long: "", points: [], who_for: [], why_choose: "", faqs: [], hero_image: "", hero_video: "", hero_video_upload: "" });

export default function ServicesManager() {
  const { toast } = useToast();
  const [groups, setGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService("core"));
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overGroup, setOverGroup] = useState(null);

  const loadGroups = () => adminApi.serviceGroups.list().then(setGroups).catch(() => {});
  const loadServices = () => { setLoading(true); adminApi.services.list().then(setServices).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { loadGroups(); loadServices(); }, []);

  const groupKeys = groups.map((g) => g.key);
  const defaultGroup = groupKeys[0] || "core";

  // Build grouped view sorted by order
  const grouped = {};
  groupKeys.forEach((k) => { grouped[k] = []; });
  grouped[UNGROUPED] = [];
  services.forEach((s) => {
    const key = grouped[s.group] ? s.group : UNGROUPED;
    grouped[key].push(s);
  });
  Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => (a.order || 99) - (b.order || 99)));

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const openCreate = (group) => { setEditing(null); setForm(emptyService(group || defaultGroup)); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...emptyService(defaultGroup), ...item, points: item.points || [], who_for: item.who_for || [], faqs: item.faqs || [] }); setOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order || 99),
        points: Array.isArray(form.points) ? form.points : String(form.points || "").split("\n").map((s) => s.trim()).filter(Boolean),
        who_for: Array.isArray(form.who_for) ? form.who_for : String(form.who_for || "").split("\n").map((s) => s.trim()).filter(Boolean),
        faqs: (form.faqs || []).filter((f) => f && (f.q || "").trim()),
      };
      if (editing) await adminApi.services.update(editing.id, payload);
      else await adminApi.services.create(payload);
      toast({ title: `Service ${editing ? "updated" : "created"}` });
      setOpen(false); loadServices();
    } catch (e) {
      toast({ title: "Error saving", description: e?.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await adminApi.services.remove(deleteId); toast({ title: "Service deleted" }); loadServices(); }
    catch { toast({ title: "Error deleting", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  // targetGroup: group key to drop into. targetIndex: position within that group (null = end)
  const performMove = async (targetGroup, targetIndex) => {
    const dragged = services.find((s) => s.id === dragId);
    setDragId(null); setOverGroup(null);
    if (!dragged || targetGroup === UNGROUPED) return;

    const newGrouped = {};
    Object.keys(grouped).forEach((k) => { newGrouped[k] = grouped[k].filter((s) => s.id !== dragId); });
    if (!newGrouped[targetGroup]) newGrouped[targetGroup] = [];
    const moved = { ...dragged, group: targetGroup };
    const idx = targetIndex == null ? newGrouped[targetGroup].length : targetIndex;
    newGrouped[targetGroup].splice(idx, 0, moved);

    const flat = [];
    const payload = [];
    groupKeys.forEach((k) => {
      (newGrouped[k] || []).forEach((s, i) => {
        flat.push({ ...s, group: k, order: i + 1 });
        payload.push({ id: s.id, group: k });
      });
    });
    (newGrouped[UNGROUPED] || []).forEach((s) => flat.push(s));
    setServices(flat);
    try { await adminApi.servicesReorder(payload); toast({ title: "Order updated" }); }
    catch { toast({ title: "Couldn't save order", variant: "destructive" }); loadServices(); }
  };

  const renderServiceRow = (s, index, groupKey) => (
    <div
      key={s.id}
      data-testid={`service-row-${s.slug || s.id}`}
      draggable
      onDragStart={() => setDragId(s.id)}
      onDragEnd={() => { setDragId(null); setOverGroup(null); }}
      onDragOver={(e) => { e.preventDefault(); setOverGroup(groupKey); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); performMove(groupKey, index); }}
      className={`bg-white rounded-xl border p-3.5 flex items-center gap-3 transition-all ${dragId === s.id ? "opacity-40" : "border-slate-100 hover:shadow-sm"}`}
    >
      <span className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-navy shrink-0 touch-none" title="Drag to reorder"><GripVertical size={17} /></span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-navy truncate">{s.title}</p>
        {s.short && <p className="text-sm text-slate-400 truncate">{s.short}</p>}
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={() => openEdit(s)} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy transition-colors"><Pencil size={15} /></button>
        <button onClick={() => setDeleteId(s.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors"><Trash2 size={15} /></button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Services</h1>
          <p className="text-sm text-slate-500">{services.length} total • drag services between groups to reorder & recategorise</p>
        </div>
        <Button onClick={() => openCreate()} data-testid="new-service-btn" className="bg-orange hover:bg-orange-dark text-white rounded-full"><Plus size={16} className="mr-1.5" /> New Service</Button>
      </div>

      <GroupsPanel groups={groups} reload={loadGroups} />

      {loading ? <p className="text-slate-400">Loading...</p> : (
        <div className="space-y-6">
          {groups.map((g) => (
            <div
              key={g.id}
              data-testid={`service-group-${g.key}`}
              onDragOver={(e) => { e.preventDefault(); setOverGroup(g.key); }}
              onDrop={(e) => { e.preventDefault(); performMove(g.key, null); }}
              className={`rounded-2xl border p-4 transition-all ${overGroup === g.key && dragId !== null ? "border-orange ring-2 ring-orange/30 bg-orange/5" : "border-slate-100 bg-slate-50/50"}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-navy flex items-center gap-2">{g.label} <span className="text-xs font-normal text-slate-400">({grouped[g.key].length})</span></h3>
                <button onClick={() => openCreate(g.key)} data-testid={`add-service-${g.key}`} className="text-xs font-semibold text-orange hover:text-orange-dark inline-flex items-center gap-1"><Plus size={13} /> Add here</button>
              </div>
              {grouped[g.key].length === 0 ? (
                <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl">Drag services here</div>
              ) : (
                <div className="space-y-2.5">{grouped[g.key].map((s, i) => renderServiceRow(s, i, g.key))}</div>
              )}
            </div>
          ))}

          {grouped[UNGROUPED].length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
              <h3 className="font-display font-bold text-amber-700 mb-1">Ungrouped services</h3>
              <p className="text-xs text-amber-600/80 mb-3">These services have no matching group. Drag them into a group above to categorise them.</p>
              <div className="space-y-2.5">{grouped[UNGROUPED].map((s, i) => renderServiceRow(s, i, UNGROUPED))}</div>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Service" : "New Service"}</DialogTitle><DialogDescription>Fill in the service details and assign it to a group.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-navy font-semibold">Title</Label><Input value={form.title} onChange={(e) => setField("title", e.target.value)} className="mt-2" /></div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-navy font-semibold">Icon</Label>
                <Select value={form.icon || ""} onValueChange={(v) => setField("icon", v)}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Choose an icon" /></SelectTrigger>
                  <SelectContent className="max-h-64">{iconNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-navy font-semibold">Group</Label>
                <Select value={form.group || ""} onValueChange={(v) => setField("group", v)}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Choose a group" /></SelectTrigger>
                  <SelectContent>{groups.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-navy font-semibold">Sort order</Label><Input type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} className="mt-2" /></div>
            </div>
            <div><Label className="text-navy font-semibold">Short description</Label><Textarea rows={2} value={form.short} onChange={(e) => setField("short", e.target.value)} className="mt-2" /><p className="text-xs text-slate-400 mt-1">The orange sub-heading under the title on the service page.</p></div>
            <div><Label className="text-navy font-semibold">Introduction</Label><Textarea rows={4} value={form.long} onChange={(e) => setField("long", e.target.value)} className="mt-2" /><p className="text-xs text-slate-400 mt-1">The opening paragraph in the hero. Leave a blank line for a new paragraph.</p></div>
            <div>
              <Label className="text-navy font-semibold">What's Included</Label>
              <Textarea rows={5} value={Array.isArray(form.points) ? form.points.join("\n") : (form.points || "")} onChange={(e) => setField("points", e.target.value.split("\n"))} className="mt-2" />
              <p className="text-xs text-slate-400 mt-1">One benefit per line (shown as ticks). Aim for five.</p>
            </div>
            <div>
              <Label className="text-navy font-semibold">Who It's For</Label>
              <Textarea rows={5} value={Array.isArray(form.who_for) ? form.who_for.join("\n") : (form.who_for || "")} onChange={(e) => setField("who_for", e.target.value.split("\n"))} className="mt-2" />
              <p className="text-xs text-slate-400 mt-1">One line per bullet. Shown after "Our {`{service}`} service is ideal if you:".</p>
            </div>
            <div>
              <Label className="text-navy font-semibold">Why Choose CASS?</Label>
              <Textarea rows={5} value={form.why_choose || ""} onChange={(e) => setField("why_choose", e.target.value)} className="mt-2" />
              <p className="text-xs text-slate-400 mt-1">Leave a blank line between paragraphs.</p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-navy font-semibold">Frequently Asked Questions</Label>
                <button type="button" data-testid="add-faq-btn" onClick={() => setField("faqs", [...(form.faqs || []), { q: "", a: "" }])} className="text-xs font-semibold text-orange hover:text-orange-dark inline-flex items-center gap-1"><Plus size={13} /> Add FAQ</button>
              </div>
              <div className="space-y-3 mt-2">
                {(form.faqs || []).map((f, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={f.q} onChange={(e) => setField("faqs", form.faqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} placeholder="Question" className="bg-white" />
                      <button type="button" onClick={() => setField("faqs", form.faqs.filter((_, j) => j !== i))} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center shrink-0"><Trash2 size={15} /></button>
                    </div>
                    <Textarea rows={2} value={f.a} onChange={(e) => setField("faqs", form.faqs.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} placeholder="Answer" className="bg-white" />
                  </div>
                ))}
                {(form.faqs || []).length === 0 && <p className="text-xs text-slate-400">No FAQs yet — click "Add FAQ" to create one.</p>}
              </div>
            </div>
            <div>
              <Label className="text-navy font-semibold">Header video (YouTube/Vimeo link)</Label>
              <Input value={form.hero_video} onChange={(e) => setField("hero_video", e.target.value)} className="mt-2" />
              <p className="text-xs text-slate-400 mt-1">Optional. Shows to the right of the page title. Takes priority over the header image if both are set.</p>
            </div>
            <VideoUpload label="Or upload a header video" value={form.hero_video_upload || ""} onChange={(v) => setField("hero_video_upload", v)} hint="Optional. MP4, max 300MB. An uploaded video takes priority over the link and image." />
            <ImageUpload label="Header image" value={form.hero_image || ""} onChange={(v) => setField("hero_image", v)} />
            <p className="text-xs text-slate-400 -mt-2">Optional. Shows to the right of the page title when no header video is set.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full"><X size={15} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-service-btn" className="bg-orange hover:bg-orange-dark text-white rounded-full"><Save size={15} className="mr-1" /> {saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this service?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
